import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, isToday } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatFullDateIST, formatTimeIST } from '../lib/dateUtils';
import {
  Phone, Search, User, Clock, Stethoscope, FileText,
  CheckCircle, ArrowRight, Loader2, Calendar as CalendarIcon,
  Users, Pill, ClipboardList, XCircle, ChevronLeft, ChevronRight, X, Zap, RefreshCw, AlertTriangle,
  Eye, Printer, Share2
} from 'lucide-react';
import { OrgFilter } from '../components/ui/org-filter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  checkin: { label: 'Check-in', color: 'bg-[#E6EFEA] text-[#0E5132] border-[rgba(14,81,50,0.15)]', icon: ClipboardList },
  history: { label: 'History', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: FileText },
  doctor_consult: { label: 'Doctor Consult', color: 'bg-violet-100 text-violet-700 border-violet-200', icon: Stethoscope },
  prescription: { label: 'Prescription', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Pill },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: CheckCircle }
};

export default function DashboardPage() {
  const navigate = useNavigate();

  // Date & search state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Existing state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [searching, setSearching] = useState(false);
  const [lookupResults, setLookupResults] = useState(null);
  const [todayCards, setTodayCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [creatingCard, setCreatingCard] = useState(false);
  const [orgFilter, setOrgFilter] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchCards = useCallback(async (date, search) => {
    setLoadingCards(true);
    try {
      const params = {};
      if (date) {
        params.date = format(date, 'yyyy-MM-dd');
      }
      if (search && search.trim()) {
        params.search = search.trim();
      }
      if (orgFilter) {
        params.org = orgFilter;
      }
      const { data } = await axios.get(`${API_URL}/api/consult-cards`, { params, withCredentials: true });
      setTodayCards(data.items || data);
    } catch (error) {
      console.error('Failed to fetch consult cards', error);
    } finally {
      setLoadingCards(false);
    }
  }, [orgFilter]);

  useEffect(() => {
    fetchCards(selectedDate, debouncedSearch);
  }, [selectedDate, debouncedSearch, fetchCards, orgFilter]);

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  const handleGoToToday = () => {
    setSelectedDate(new Date());
    setCalendarOpen(false);
  };

  const handlePrevDay = () => {
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    setSelectedDate(prev);
  };

  const handleNextDay = () => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    setSelectedDate(next);
  };

  const handlePhoneLookup = async () => {
    const clean = phoneNumber.replace(/\D/g, '');
    if (clean.length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/lookup`, {
        params: { phone: clean },
        withCredentials: true
      });
      setLookupResults(data);
      const totalFound = (data.profiles?.length || 0) + (data.eden_profiles?.length || 0);
      if (totalFound === 0) {
        toast.info('No profiles found. Create a new patient.');
      }
    } catch (error) {
      toast.error('Lookup failed');
    } finally {
      setSearching(false);
    }
  };

  const handlePickEdenPatient = async (edenProfile) => {
    setCreatingCard(true);
    try {
      await axios.post(`${API_URL}/api/patients/from-eden`, edenProfile, { withCredentials: true });
      toast.success(`Visit started for ${edenProfile.name}`);
      setLookupResults(null);
      setPhoneNumber('');
      fetchCards(selectedDate, debouncedSearch);
    } catch (error) {
      toast.error('Failed to create visit');
    } finally {
      setCreatingCard(false);
    }
  };

  const handleCloseCard = async (e, cardId, patientName) => {
    e.stopPropagation();
    if (!window.confirm(`Close visit for ${patientName}?`)) return;
    try {
      await axios.put(`${API_URL}/api/consult-cards/${cardId}`, { stage: 'closed' }, { withCredentials: true });
      toast.success('Visit closed');
      fetchCards(selectedDate, debouncedSearch);
    } catch (error) {
      toast.error('Failed to close visit');
    }
  };

  const handleReopenCard = async (e, cardId, patientName) => {
    e.stopPropagation();
    try {
      await axios.put(`${API_URL}/api/consult-cards/${cardId}`, { stage: 'checkin' }, { withCredentials: true });
      toast.success(`Reopened visit for ${patientName}`);
      fetchCards(selectedDate, debouncedSearch);
    } catch (error) {
      toast.error('Failed to reopen visit');
    }
  };

  const activeCards = todayCards.filter(c => c.stage !== 'closed');
  const closedCards = todayCards.filter(c => c.stage === 'closed');
  const showingToday = isToday(selectedDate);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header with Date Navigation */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-800" data-testid="dashboard-title">
            {showingToday ? "Today's Dashboard" : 'Dashboard'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {formatFullDateIST(selectedDate.toISOString())}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <OrgFilter value={orgFilter} onChange={setOrgFilter} />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-slate-400 hover:text-[#0E5132]"
            onClick={() => fetchCards(selectedDate, debouncedSearch)}
            title="Refresh"
            data-testid="refresh-dashboard-button"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Badge variant="outline" className="gap-1.5 px-3 py-1.5 border-[rgba(14,81,50,0.15)] bg-[#E6EFEA] text-[#0E5132]" data-testid="active-count-badge">
            <Users className="w-3.5 h-3.5" />
            {activeCards.length} Active
          </Badge>
          <Badge variant="outline" className="gap-1.5 px-3 py-1.5 border-slate-200 bg-slate-50 text-slate-600" data-testid="closed-count-badge">
            <CheckCircle className="w-3.5 h-3.5" />
            {closedCards.length} Closed
          </Badge>
        </div>
      </div>

      {/* Date Picker + Search Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-testid="filter-toolbar">
        {/* Date Navigation */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-lg"
            onClick={handlePrevDay}
            data-testid="prev-day-button"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-9 px-3 rounded-lg gap-2 font-medium text-sm min-w-[160px] justify-start"
                data-testid="date-picker-trigger"
              >
                <CalendarIcon className="w-4 h-4 text-[#0E5132]" />
                {format(selectedDate, 'dd MMM yyyy')}
                {showingToday && (
                  <Badge className="ml-1 h-5 px-1.5 text-[10px] bg-blue-600 hover:bg-blue-600">Today</Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                initialFocus
              />
              {!showingToday && (
                <div className="border-t px-3 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-[#0E5132] hover:text-[#0E5132] hover:bg-[#E6EFEA]"
                    onClick={handleGoToToday}
                    data-testid="go-to-today-button"
                  >
                    Go to Today
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-lg"
            onClick={handleNextDay}
            data-testid="next-day-button"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, phone, or notes..."
            className="pl-9 pr-9 h-9 rounded-lg"
            data-testid="dashboard-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="clear-search-button"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Phone Lookup - Primary Action */}
      <Card className="border-2 border-[rgba(14,81,50,0.15)] bg-gradient-to-r from-blue-50/50 to-white shadow-sm" data-testid="phone-lookup-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#0E5132]" />
            Start New Visit
          </CardTitle>
          <CardDescription>Enter patient's phone number to begin</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 items-end">
            <div className="w-24">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Code</Label>
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger className="rounded-xl" data-testid="country-code-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="+91">+91</SelectItem>
                  <SelectItem value="+1">+1</SelectItem>
                  <SelectItem value="+44">+44</SelectItem>
                  <SelectItem value="+971">+971</SelectItem>
                  <SelectItem value="+65">+65</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Phone Number</Label>
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10-digit number"
                className="rounded-xl text-lg tracking-wider h-11"
                data-testid="phone-input"
                onKeyDown={(e) => e.key === 'Enter' && handlePhoneLookup()}
              />
            </div>
            <Button
              onClick={handlePhoneLookup}
              disabled={searching || phoneNumber.length < 10}
              className="rounded-xl h-11 px-6 bg-[#0E5132] hover:bg-[#063D2F]"
              data-testid="phone-lookup-button"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Lookup
            </Button>
          </div>

          {/* Lookup Results — Eden is source of truth */}
          {lookupResults && (
            <div className="mt-4 space-y-3" data-testid="lookup-results">
              {(lookupResults.eden_profiles?.length > 0 || lookupResults.profiles?.length > 0) ? (
                <>
                  {lookupResults.eden_profiles?.length > 0 && (
                    <>
                      <p className="text-sm font-medium text-slate-700">
                        {lookupResults.eden_profiles.length} patient{lookupResults.eden_profiles.length !== 1 ? 's' : ''} found in Eden
                      </p>
                      <div className="grid gap-2">
                        {lookupResults.eden_profiles.map((ep) => (
                          <div
                            key={ep.eden_patient_id}
                            className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white hover:border-[rgba(14,81,50,0.25)] hover:bg-[#E6EFEA]/30 transition-colors"
                            data-testid={`eden-profile-${ep.eden_patient_id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-[#E6EFEA] flex items-center justify-center">
                                <User className="w-5 h-5 text-[#0E5132]" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-sm">{ep.name}</p>
                                  {ep.tags?.includes('VIP') && (
                                    <Badge className="h-4 px-1.5 text-[9px] bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">VIP</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {ep.age ? `${ep.age}yo` : ''} {ep.gender !== 'other' ? ep.gender : ''}
                                  {ep.phone && ` · ${ep.phone}`}
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handlePickEdenPatient(ep)}
                              disabled={creatingCard}
                              className="rounded-full bg-[#0E5132] hover:bg-[#063D2F]"
                              data-testid={`pick-eden-${ep.eden_patient_id}`}
                            >
                              {creatingCard ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}
                              Start Visit
                            </Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {lookupResults.profiles?.length > 0 && (
                    <>
                      {lookupResults.eden_profiles?.length > 0 && (
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 pt-1">
                          <div className="flex-1 h-px bg-slate-200" />
                          <span>also found locally</span>
                          <div className="flex-1 h-px bg-slate-200" />
                        </div>
                      )}
                      {!lookupResults.eden_profiles?.length && (
                        <p className="text-sm font-medium text-slate-700">
                          {lookupResults.profiles.length} patient{lookupResults.profiles.length !== 1 ? 's' : ''} found locally
                        </p>
                      )}
                      <div className="grid gap-2">
                        {lookupResults.profiles.map((lp) => (
                          <div
                            key={lp.id}
                            className="flex items-center justify-between p-3 rounded-xl border border-blue-200 bg-blue-50/30 hover:border-blue-300 transition-colors"
                            data-testid={`local-profile-${lp.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{lp.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {lp.age ? `${lp.age}yo` : ''} {lp.gender !== 'other' ? lp.gender : ''}
                                  {lp.phone && ` · ${lp.country_code} ${lp.phone}`}
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/patients/${lp.id}`)}
                              className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-100"
                              data-testid={`view-local-${lp.id}`}
                            >
                              <ArrowRight className="w-3.5 h-3.5 mr-1" /> View Profile
                            </Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-center">
                  <p className="text-sm text-amber-700 font-medium">No patient found with this number</p>
                  <p className="text-xs text-amber-600 mt-1">Please register the patient in the Eden Appointment System first</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Visits */}
      <div>
        <h2 className="text-base font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-[#0E5132]" />
          Active Visits ({activeCards.length})
          {debouncedSearch && (
            <Badge variant="secondary" className="text-xs ml-1" data-testid="search-indicator">
              Filtered: "{debouncedSearch}"
            </Badge>
          )}
        </h2>
        {loadingCards ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-[#0E5132]" />
          </div>
        ) : activeCards.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-8 text-center">
              <ClipboardList className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {debouncedSearch
                  ? `No visits matching "${debouncedSearch}" on this date.`
                  : showingToday
                    ? 'No active visits today. Start by entering a phone number above.'
                    : 'No visits found for this date.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {activeCards.map((card) => {
              const stageConf = STAGE_CONFIG[card.stage] || STAGE_CONFIG.checkin;
              const StageIcon = stageConf.icon;
              return (
                <Card
                  key={card.id}
                  className={`border transition-colors shadow-sm cursor-pointer ${
                    card.eden_source
                      ? 'border-emerald-200 hover:border-emerald-300 bg-emerald-50/20'
                      : 'border-slate-200 hover:border-[rgba(14,81,50,0.15)]'
                  }`}
                  data-testid={`consult-card-${card.id}`}
                  onClick={() => navigate(`/consult-cards/${card.id}`)}
                >
                  <CardContent className="py-3 sm:py-4 px-4 sm:px-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0" onClick={() => navigate(`/consult-cards/${card.id}`)}>
                        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${
                          card.eden_source ? 'bg-emerald-100' : 'bg-[#E6EFEA]'
                        }`}>
                          <User className={`w-4 h-4 sm:w-5 sm:h-5 ${card.eden_source ? 'text-emerald-600' : 'text-[#0E5132]'}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-heading font-medium text-sm truncate">{card.patient_name}</p>
                            {card.eden_source && (
                              <Badge className="h-5 px-1.5 text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 gap-0.5" data-testid={`eden-badge-${card.id}`}>
                                <Zap className="w-2.5 h-2.5" />
                                Eden
                              </Badge>
                            )}
                            {card.needs_attention && (
                              <Badge className="h-5 px-1.5 text-[10px] bg-red-100 text-red-600 border-red-200 hover:bg-red-100 animate-pulse gap-0.5">
                                <AlertTriangle className="w-2.5 h-2.5" /> Reasoning
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                            <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{card.patient_phone}</span>
                            <span className="hidden sm:inline">&middot;</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTimeIST(card.created_at)}</span>
                            {card.eden_service_name && (
                              <span className="text-emerald-600 font-medium hidden sm:inline">· {card.eden_service_name}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2 ml-12 sm:ml-0 flex-wrap">
                        {card.assigned_doctor_name && (
                          <Badge variant="outline" className="text-[10px] sm:text-xs gap-1 border-violet-200 bg-violet-50 text-violet-700">
                            <Stethoscope className="w-3 h-3" />
                            <span className="hidden sm:inline">{card.assigned_doctor_name}</span>
                            <span className="sm:hidden">{card.assigned_doctor_name.split(' ')[0]}</span>
                          </Badge>
                        )}
                        <Badge variant="outline" className={`gap-1 text-[10px] sm:text-xs ${stageConf.color}`}>
                          <StageIcon className="w-3 h-3" />
                          {stageConf.label}
                        </Badge>
                        {card.ai_prescription_draft && !card.ai_prescription_draft?.insufficient_data && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                onClick={(e) => e.stopPropagation()}
                                className="h-5 px-1.5 text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200 gap-0.5 inline-flex items-center rounded-full font-medium transition-colors cursor-pointer"
                                data-testid={`rx-btn-${card.id}`}
                              >
                                <Pill className="w-2.5 h-2.5" /> Rx
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-44 p-1.5" align="end" onClick={(e) => e.stopPropagation()}>
                              <div className="space-y-0.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf`, '_blank');
                                  }}
                                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                                  data-testid={`rx-view-${card.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5 text-slate-500" /> View PDF
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const res = await fetch(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf?print_mode=true`, { credentials: 'include' });
                                      const blob = await res.blob();
                                      const url = URL.createObjectURL(blob);
                                      const pf = document.createElement('iframe');
                                      pf.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
                                      pf.src = url;
                                      document.body.appendChild(pf);
                                      pf.onload = () => { setTimeout(() => { pf.contentWindow.print(); setTimeout(() => { document.body.removeChild(pf); URL.revokeObjectURL(url); }, 2000); }, 300); };
                                    } catch { toast.error('Print failed'); }
                                  }}
                                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                                  data-testid={`rx-print-${card.id}`}
                                >
                                  <Printer className="w-3.5 h-3.5 text-slate-500" /> Print
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const res = await fetch(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf`, { credentials: 'include' });
                                      const blob = await res.blob();
                                      const file = new File([blob], `prescription_${card.patient_name?.replace(/\s+/g,'_')}.pdf`, { type: 'application/pdf' });
                                      if (navigator.share && navigator.canShare?.({ files: [file] })) {
                                        await navigator.share({ files: [file], title: `Prescription - ${card.patient_name}` });
                                      } else {
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a'); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url);
                                        toast.success('Prescription downloaded');
                                      }
                                    } catch (err) { if (err.name !== 'AbortError') toast.error('Share failed'); }
                                  }}
                                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                                  data-testid={`rx-share-${card.id}`}
                                >
                                  <Share2 className="w-3.5 h-3.5 text-slate-500" /> Share
                                </button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <button
                          onClick={(e) => handleCloseCard(e, card.id, card.patient_name)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Close visit"
                          data-testid={`close-card-${card.id}`}
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {card.notes && (
                      <p className="text-xs text-muted-foreground mt-2 ml-14">{card.notes}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Closed Cards — clickable to view */}
      {closedCards.length > 0 && (
        <div>
          <h2 className="text-base font-heading font-semibold text-slate-600 mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-slate-400" />
            Completed ({closedCards.length})
          </h2>
          <div className="grid gap-2">
            {closedCards.map((card) => (
              <Card key={card.id} className="border border-slate-100 bg-slate-50/50 hover:bg-slate-50 hover:border-slate-200 transition-colors cursor-pointer" data-testid={`closed-card-${card.id}`}
                onClick={() => navigate(`/consult-cards/${card.id}`)}
              >
                <CardContent className="py-3 px-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-slate-400" />
                      <p className="text-sm text-slate-600">{card.patient_name}</p>
                      <span className="text-xs text-muted-foreground">{card.patient_country_code} {card.patient_phone}</span>
                      {card.eden_service_name && (
                        <span className="text-xs text-emerald-600">{card.eden_service_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {card.assigned_doctor_name && (
                        <span className="text-xs text-muted-foreground">{card.assigned_doctor_name}</span>
                      )}
                      <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">Closed</Badge>
                      {card.ai_prescription_draft && !card.ai_prescription_draft?.insufficient_data && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="h-5 px-1.5 text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200 gap-0.5 inline-flex items-center rounded-full font-medium transition-colors cursor-pointer"
                              data-testid={`rx-btn-closed-${card.id}`}
                            >
                              <Pill className="w-2.5 h-2.5" /> Rx
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-44 p-1.5" align="end" onClick={(e) => e.stopPropagation()}>
                            <div className="space-y-0.5">
                              <button
                                onClick={(e) => { e.stopPropagation(); window.open(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf`, '_blank'); }}
                                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5 text-slate-500" /> View PDF
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const res = await fetch(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf?print_mode=true`, { credentials: 'include' });
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    const pf = document.createElement('iframe');
                                    pf.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
                                    pf.src = url;
                                    document.body.appendChild(pf);
                                    pf.onload = () => { setTimeout(() => { pf.contentWindow.print(); setTimeout(() => { document.body.removeChild(pf); URL.revokeObjectURL(url); }, 2000); }, 300); };
                                  } catch { toast.error('Print failed'); }
                                }}
                                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                              >
                                <Printer className="w-3.5 h-3.5 text-slate-500" /> Print
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const res = await fetch(`${API_URL}/api/consult-cards/${card.id}/prescription-pdf`, { credentials: 'include' });
                                    const blob = await res.blob();
                                    const file = new File([blob], `prescription_${card.patient_name?.replace(/\s+/g,'_')}.pdf`, { type: 'application/pdf' });
                                    if (navigator.share && navigator.canShare?.({ files: [file] })) {
                                      await navigator.share({ files: [file], title: `Prescription - ${card.patient_name}` });
                                    } else {
                                      const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url);
                                      toast.success('Prescription downloaded');
                                    }
                                  } catch(err) { if(err.name !== 'AbortError') toast.error('Share failed'); }
                                }}
                                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                              >
                                <Share2 className="w-3.5 h-3.5 text-slate-500" /> Share
                              </button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReopenCard(e, card.id, card.patient_name); }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-[#0E5132] hover:bg-[#E6EFEA] transition-colors"
                        title="Reopen visit"
                        data-testid={`reopen-card-${card.id}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
